/**
 * Décisions pures de la négociation WebRTC — extraites pour être testables
 * sans `RTCPeerConnection` (jsdom n'implémente pas WebRTC).
 */

/**
 * Tie-break déterministe "perfect negotiation" : exactement un des deux pairs
 * est "poli" (cède en cas de glare — offre entrante pendant qu'on négocie déjà)
 * — comparaison lexicographique des userId, symétrique et sans coordination.
 */
export function isPolite(localUserId: string, remoteUserId: string): boolean {
  return localUserId < remoteUserId;
}

export const MAX_ICE_RESTART_ATTEMPTS = 2;

/** Faut-il retenter un restart ICE après cette tentative (1-indexée) ? */
export function shouldAttemptIceRestart(
  attempt: number,
  maxAttempts: number = MAX_ICE_RESTART_ATTEMPTS,
): boolean {
  return attempt <= maxAttempts;
}

export type AggregateConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'failed';

export interface PeerConnectionSnapshot {
  connectionState: string;
  /** Les tentatives de redémarrage ICE de ce pair sont épuisées. */
  restartsExhausted: boolean;
}

/**
 * État de connexion GLOBAL d'une salle à plusieurs, déduit de celui de chaque pair.
 * Avec N pairs, la panne d'UN pair ne doit pas déclarer toute la salle en échec :
 *  - au moins un pair connecté → « connected » ;
 *  - sinon au moins un pair qui retente (failed/disconnected avec tentatives restantes)
 *    → « reconnecting » ;
 *  - sinon tous les pairs sont en échec définitif → « failed » ;
 *  - sinon (négociation en cours) → « connecting ».
 * `null` = aucun pair : on garde l'état courant (seul dans la salle).
 * Avec UN pair, c'est exactement le comportement historique du 1:1.
 */
export function aggregateConnectionState(
  peers: readonly PeerConnectionSnapshot[],
): AggregateConnectionState | null {
  if (peers.length === 0) return null;
  if (peers.some((p) => p.connectionState === 'connected')) return 'connected';
  const isDown = (p: PeerConnectionSnapshot) =>
    p.connectionState === 'failed' || p.connectionState === 'disconnected';
  if (peers.some((p) => isDown(p) && !p.restartsExhausted)) return 'reconnecting';
  if (peers.every((p) => isDown(p) && p.restartsExhausted)) return 'failed';
  return 'connecting';
}
